/**
 * What became of a user's latest letter (#114) — the decisions behind the Usuarios line and dialog.
 *
 * The server reads SES's events and says what happened (`User.lastEmail`, `EmailDelivery` in the
 * contract); this file decides only how loudly to say it, which words to use, and how long to keep
 * asking. It imports no React, so each rule is a plain function a test can call.
 */

import type { EmailDelivery, User } from '@deca/api'

export type DeliveryTone = 'success' | 'info' | 'warning' | 'danger'

/** Nothing reached the person, or it did and they called it spam: the administrator has to act. */
export function isDeliveryFailure(delivery: EmailDelivery): boolean {
  return (
    delivery.status === 'BOUNCED' ||
    delivery.status === 'REJECTED' ||
    delivery.status === 'FAILED' ||
    delivery.status === 'COMPLAINED'
  )
}

/** Still on its way: the next event will say more. */
export function isDeliveryPending(delivery: EmailDelivery): boolean {
  return delivery.status === 'SENDING' || delivery.status === 'SENT' || delivery.status === 'DELAYED'
}

export function deliveryTone(delivery: EmailDelivery): DeliveryTone {
  switch (delivery.status) {
    case 'DELIVERED':
      return 'success'
    case 'SENDING':
    case 'SENT':
      return 'info'
    case 'DELAYED':
    case 'COMPLAINED':
      return 'warning'
    default:
      return 'danger'
  }
}

/**
 * The catalogue key of the dialog's explanation: one per reason for a bounce, one per status
 * otherwise. The server's `reason` is a closed set, so a key always exists; `OTHER` stands in for a
 * bounce that somehow arrives without one.
 */
export function explanationKey(delivery: EmailDelivery): string {
  if (delivery.status === 'BOUNCED') return `admin.users.mail.reason.${delivery.reason ?? 'OTHER'}`
  return `admin.users.mail.explain.${delivery.status}`
}

/**
 * The letter the Usuarios list shows under a user, if any.
 *
 * A delivery is news only while the person has not signed in yet: it says the invitation is in
 * their inbox. For everybody else it is the ordinary case, and a column of "delivered" under
 * active accounts would bury the one line that matters — a letter that did not arrive, which is
 * shown whoever it was for.
 */
export function mailLineFor(user: User): EmailDelivery | null {
  const delivery = user.lastEmail ?? null
  if (!delivery) return null
  if (delivery.status === 'DELIVERED' && user.status !== 'INVITED') return null
  return delivery
}

/** A problem with the address itself, which changing the address is what fixes. */
export function isAddressProblem(delivery: EmailDelivery): boolean {
  return delivery.status === 'BOUNCED' && (delivery.reason === 'MAILBOX_NOT_FOUND' || delivery.reason === 'ADDRESS_SUPPRESSED')
}

export const POLL_MS = 4_000

/** How long a letter may stay pending before the list stops asking about it on its own. */
const PENDING_WINDOW_MS = 10 * 60_000

/**
 * How often the users list should refetch: every few seconds while any letter on the page is on its
 * way, or while the office has just sent one (`watchUntil`), and never otherwise.
 *
 * The watch window exists because the letter is recorded by the send itself, which the server runs
 * after answering: the refetch that follows "Enviar restablecimiento" can arrive before the new row
 * exists, and would then show the previous letter's final status with nothing pending to poll for.
 */
export function mailPollInterval(users: readonly User[], now: number, watchUntil: number): number | false {
  if (now < watchUntil) return POLL_MS
  const waiting = users.some(
    (user) =>
      user.lastEmail != null &&
      isDeliveryPending(user.lastEmail) &&
      now - Date.parse(user.lastEmail.sentAt) < PENDING_WINDOW_MS,
  )
  return waiting ? POLL_MS : false
}

/** How long the list keeps asking after the office sends a letter. */
export const WATCH_AFTER_SEND_MS = 60_000
